import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmAction,
  DetailPage,
  EmptyState,
  Skeleton,
  StatusBadge,
} from '@skylabs-digital/backoffice-kit';
import { useSnackbar } from '@skylabs-digital/react-proto-kit';
import { Ban, Files, RotateCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { appsApi, useAppActions } from '../api/apps.api';
import { formatBytes } from '../components/apps/AppList';
import { PrefixRulesCard } from '../components/apps/PrefixRulesCard';
import { StorageStatsCard } from '../components/apps/StorageStatsCard';
import { WebhooksCard } from '../components/apps/WebhooksCard';
import { isManagedByCac, ManagedByCacBadge } from '../components/shared/ManagedByCac';
import type { AppStats } from '../schemas/app.schema';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-4 border-b border-border py-3 text-sm last:border-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="col-span-2 text-foreground">{children}</dd>
    </div>
  );
}

export function AppDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { data: app, loading, error, refetch } = appsApi.useById(id ?? '');
  const actions = useAppActions();
  const [stats, setStats] = useState<AppStats | null>(null);

  // Las stats se recargan cuando cambia la app (orphan/reactivate refetchea).
  const appVersion = app?.updatedAt;
  useEffect(() => {
    if (!id || !appVersion) return;
    actions
      .stats(id)
      .then(setStats)
      .catch(() => setStats(null));
  }, [id, appVersion, actions.stats]);

  if (loading) {
    return (
      <DetailPage title="App" breadcrumbs={[{ label: 'Apps', to: '/apps' }, { label: '…' }]}>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </DetailPage>
    );
  }
  if (error || !app || !id) {
    return (
      <DetailPage title="App" breadcrumbs={[{ label: 'Apps', to: '/apps' }]}>
        <EmptyState
          title="App not found"
          description={
            error instanceof Error ? error.message : 'This app may not exist in Fisto yet.'
          }
          action={<Button onClick={() => navigate('/apps')}>Back to apps</Button>}
        />
      </DetailPage>
    );
  }

  const managed = isManagedByCac(app);
  const flip = async () => {
    try {
      if (app.status === 'ORPHAN') await actions.reactivate(id);
      else await actions.orphan(id);
      showSnackbar({
        message: app.status === 'ORPHAN' ? 'App reactivated' : 'App orphaned: writes are blocked',
        variant: 'success',
      });
      refetch();
    } catch {
      showSnackbar({ message: 'Failed to change the app status', variant: 'error' });
    }
  };

  return (
    <DetailPage
      title={app.name}
      description={
        <span className="flex flex-wrap items-center gap-2">
          <StatusBadge tone={app.status === 'ORPHAN' ? 'danger' : 'success'}>
            {app.status === 'ORPHAN' ? 'Orphan' : 'Active'}
          </StatusBadge>
          {managed ? <ManagedByCacBadge /> : null}
        </span>
      }
      breadcrumbs={[{ label: 'Apps', to: '/apps' }, { label: app.name }]}
      actions={
        <div className="flex items-center gap-2">
          <ConfirmAction
            trigger={
              <Button variant={app.status === 'ORPHAN' ? 'default' : 'outline'}>
                {app.status === 'ORPHAN' ? <RotateCw /> : <Ban />}
                {app.status === 'ORPHAN' ? 'Reactivate' : 'Orphan'}
              </Button>
            }
            title={app.status === 'ORPHAN' ? 'Reactivate this app?' : 'Orphan this app?'}
            description={
              app.status === 'ORPHAN'
                ? 'Uploads and deletes will be accepted again.'
                : 'Every write is rejected while orphaned. Existing files stay readable.'
            }
            confirmLabel={app.status === 'ORPHAN' ? 'Reactivate' : 'Orphan'}
            destructive={app.status !== 'ORPHAN'}
            onConfirm={flip}
          />
          <Button variant="outline" onClick={() => navigate('/apps')}>
            Back
          </Button>
        </div>
      }
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>App</CardTitle>
          </CardHeader>
          <CardContent>
            <dl>
              <Field label="idachu app id">
                <code className="font-mono text-xs">{app.id}</code>
              </Field>
              <Field label="Quota">{formatBytes(app.storageQuotaBytes)}</Field>
              <Field label="Managed by">{managed ? 'CaC (cac/stack.ts)' : 'manual'}</Field>
              <Field label="Created">{new Date(app.createdAt).toLocaleString()}</Field>
            </dl>
          </CardContent>
        </Card>
        <StorageStatsCard appId={id} stats={stats} />
      </div>
      <PrefixRulesCard rules={app.prefixRules} managed={managed} />
      <WebhooksCard appId={id} />
      <Card>
        <CardHeader>
          <CardTitle>Files</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {stats ? `${stats.fileCount} files, ${formatBytes(stats.bytesUsed)} used. ` : ''}
            Originals, variants and pending uploads are browsed in the Files section.
          </p>
          <Button variant="outline" onClick={() => navigate(`/files?app=${id}`)}>
            <Files />
            Browse files
          </Button>
        </CardContent>
      </Card>
    </DetailPage>
  );
}
